import * as React from "react";
import { styled, Theme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MainListItems from "./AreaList/MainListItems";
import SecondaryListItems from "./AreaList/SecondaryListItems";
import AdminListItems from "./AreaList/AdminListItems";
import {
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Button,
  Badge,
  ListItem,
  ListItemText,
  Popover,
  Card,
  CardContent,
} from "@mui/material";
// Convert heavy, non-initially needed components to lazy-loaded modules
import useToken from "../useToken";
import jwt_decode from "jwt-decode";
import { ISignInData } from "../../services/API/signin";
import "@mui/styles";
import { useNavigate } from "react-router-dom";
import GetImage from "../../services/API/GetImage";
import { useEffect, Suspense } from "react";
import { Fab } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import GetExpenseRequests, {
  IExpenseRequestRow,
} from "../../services/API/GetExpenseRequests";
import InfoIcon from "@mui/icons-material/Info";
import { useThemeMode } from "../../theme/ThemeModeContext";
import useMediaQuery from "@mui/material/useMediaQuery";
// Performance instrumentation marks
if (typeof performance !== "undefined") {
  performance.mark("dashboard_module_loaded");
}

// Lazy imports (code splitting) for components not required at first paint
const ClaimWizard = React.lazy(() => import("../../Common/ClaimWizard"));
const AddExpenseRequest = React.lazy(
  () => import("./Expense/AddExpenseRequest"),
);
const DashboardPreview = React.lazy(() => import("./DashboardPreview"));
const UserProfilePage = React.lazy(
  () => import("../UserProfile/UserProfilePage"),
);

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const drawerWidth: number = 240;
//SessionTimeout();
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    boxShadow: theme.shadows[8],
    borderRadius: "16px",
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${theme.palette.background.paper} 80%, ${theme.palette.primary.dark} 100%)`
        : `linear-gradient(135deg, ${theme.palette.background.paper} 80%, ${theme.palette.primary.light} 100%)`,
    color: theme.palette.text.primary,
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

const getFilteredDataAndCount = (
  data: IExpenseRequestRow[],
  userData: ISignInData,
) => {
  let newDate = [];

  if (
    userData.CanPreApproveExpense === "True" ||
    userData.CanApproveExpense === "True"
  ) {
    newDate = data?.filter((request) => request.requestStatusOrder === 2);
  } else if (userData.CanReviewExpense === "True") {
    newDate = data.filter(
      (request) =>
        request.requestStatusOrder === 1 || request.requestStatusOrder === 5,
    );
  } else {
    newDate = data.filter((request) => request.approvedBy === null);
  }
  //Hide Request from assigned Person until request is approved
  const filteredData = newDate.filter((item) => {
    const condition = !(
      item.requestAssignedID === Number(userData.EmployeeID) &&
      item.requestStatusOrder === 1
    );
    return condition;
  });
  return {
    newDate: filteredData,
    count: filteredData.length,
  };
};
const DashboardContent = () => {
  if (typeof performance !== "undefined") {
    performance.mark("dashboard_content_render_start");
  }
  const { mode, setMode } = useThemeMode();
  const navigate = useNavigate();
  const token = useToken().token;
  // Memoize decoded user data so its reference is stable and does not retrigger effects unnecessarily
  const userData = React.useMemo(
    () => jwt_decode(token) as ISignInData,
    [token],
  );
  const [userImageToDisplay, setUserImageToDisplay] = React.useState<
    string | undefined
  >(undefined);
  const mobileBreakpoint = 600; // Example breakpoint for mobile devices
  const isMobile = window.innerWidth < mobileBreakpoint;
  const isMobileScreen = useMediaQuery("(max-width:600px)");
  const [openClaim, onClose] = React.useState(false);
  const [openPR, setOpenPR] = React.useState<boolean>(false);
  const [data, setData] = React.useState<IExpenseRequestRow[]>();
  const [requestRow, setRequestRow] = React.useState<IExpenseRequestRow>();
  const [reload, setReload] = React.useState(false);
  const [badgeContent, setBadgeContent] = React.useState(0);

  const defer = (cb: () => void) => {
    if (typeof (window as any).requestIdleCallback === "function") {
      (window as any).requestIdleCallback(cb);
    } else {
      setTimeout(cb, 150);
    }
  };

  useEffect(() => {
    const id = userData.EmployeeID;

    GetExpenseRequests({ token, id, notificationMode: true }).then(
      (response) => {
        if (response) {
          // Server already excludes old Approved/Completed rows when notificationMode is true
          const { newDate, count } = getFilteredDataAndCount(
            response.data,
            userData,
          );
          setData(newDate);
          setBadgeContent(count);
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userData.EmployeeID, reload]);

  useEffect(() => {
    defer(() => {
      const subfolder = "Profile";
      const imageName = userData.UserImageName;
      GetImage({ token, subfolder, imageName })
        .then((imageResponse) => {
          if (imageResponse) {
            const blob = new Blob([imageResponse.data], {
              type: "image/jpeg",
            });
            const imageUrl = URL.createObjectURL(blob);
            setUserImageToDisplay(imageUrl);
          }
        })
        .catch((imageError) => {
          console.error("Error fetching image:", imageError);
        });
    });
  }, [token, userData.UserImageName]);

  const logOut = () => {
    sessionStorage.clear();
    navigate("/Login");
  };
  const [open, setOpen] = React.useState(isMobile ? false : true);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );
  const [areaToLoad, setAreaToLoad] = React.useState<JSX.Element>(
    // Initial dashboard preview is lazy-loaded
    <DashboardPreview />,
  );
  const [isWizardOpen, setWizardOpen] = React.useState<boolean>(false);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const handleProfileClicked = () => {
    setAreaToLoad(<UserProfilePage />);
  };
  const handleLogoutClicked = () => {
    logOut();
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleOpenWizard = () => {
    onClose(true);
    setWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
  };

  const HandleOpenPR = () => {
    setReload(false);
    setRequestRow(undefined);
    setOpenPR(true);
  };
  const handleClose = (val?: boolean) => {
    if (val) setReload(true);
    setRequestRow(undefined);
    setOpenPR(false);
  };

  const [anchorElNotification, setAnchorElNotification] = React.useState(null);

  const handleClick = (event: any) => {
    setAnchorElNotification(event.currentTarget);
  };

  const handleCloseNotification = () => {
    setAnchorElNotification(null);
  };
  const loadFullRequest = (request: IExpenseRequestRow) => {
    setReload(false);
    setAnchorElNotification(null);
    setRequestRow(request);
    setOpenPR(true);
  };

  const openNotification = Boolean(anchorElNotification);
  const id = open ? "simple-popover" : undefined;
  return (
    <Box sx={{ display: "flex" }}>
      {typeof performance !== "undefined" &&
        (() => {
          performance.mark("dashboard_content_before_return");
          return null;
        })()}
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={4}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.primary.main
              : theme.palette.background.paper,
          color: (theme) =>
            theme.palette.mode === "light"
              ? "#fff"
              : theme.palette.text.primary,
          boxShadow: 3,
        }}
      >
        <Toolbar sx={{ pr: "24px" }}>
          <Tooltip title={open ? "Close sidebar" : "Open sidebar"}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="toggle drawer"
              onClick={toggleDrawer}
              sx={{ marginRight: "36px" }}
              size="large"
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          {!isMobileScreen && (
            <Typography
              component="h1"
              variant="h6"
              color="inherit"
              noWrap
              sx={{ flexGrow: 1 }}
            >
              {`SLICO Dashboard  |  `}
              <Button
                color="error"
                variant="contained"
                style={{ textTransform: "none" }}
                startIcon={<BookOnlineIcon />}
                onClick={HandleOpenPR}
              >
                Expense Request
              </Button>
            </Typography>
          )}

          <Fab
            color="secondary"
            style={{ position: "fixed", bottom: "16px", right: "16px" }}
            onClick={handleOpenWizard}
          >
            <AddIcon />
          </Fab>
          <div
            style={{
              position: "absolute",
              right: "70px",
            }}
          >
            <Box sx={{ flexGrow: 0 }}>
              {/* Dark mode toggle button */}
              <IconButton
                sx={{ ml: 1 }}
                onClick={() => setMode(mode === "light" ? "dark" : "light")}
                color="inherit"
              >
                <span style={{ fontSize: "0.85rem" }}>
                  {mode === "dark" ? "🌞 Light" : "🌙 Dark"}
                </span>
              </IconButton>
              <Tooltip title="Notification">
                <Badge
                  badgeContent={badgeContent}
                  color="error"
                  onClick={handleClick}
                  sx={{ cursor: "pointer" }}
                >
                  <NotificationsNoneIcon />
                </Badge>
              </Tooltip>
              {data && data.length > 0 && (
                <Popover
                  id={id}
                  open={openNotification}
                  anchorEl={anchorElNotification}
                  onClose={handleCloseNotification}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <List
                    sx={{
                      bgcolor: "grey.200",
                      maxHeight: "400px", // Adjust the height as needed
                      width: "400px",
                      overflowY: "auto",
                      "&::-webkit-scrollbar": {
                        width: "1.5px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#888",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#555",
                      },
                      cursor: "pointer",
                    }}
                  >
                    {data?.map((request, index) => {
                      let statusColor = "orange"; // Default color
                      if (request.requestStatus === "Awaiting Approval") {
                        statusColor = "dodgerblue"; // A brighter blue for better visibility
                      } else if (request.requestStatus === "Approved") {
                        statusColor = "forestgreen"; // A deep green for a positive status
                      } else if (request.requestStatus === "Completed") {
                        statusColor = "mediumpurple"; // A medium purple for distinction
                      } else if (request.requestStatus === "Rejected") {
                        statusColor = "crimson"; // A strong red for alert
                      } else if (request.requestStatus === "Closed") {
                        statusColor = "dimgray"; // A medium gray for a neutral status
                      } else if (request.requestStatus === "Queried") {
                        statusColor = "#088da5"; // A distinct color for the Queried status
                      }

                      return (
                        <Card
                          key={index}
                          sx={(theme) => ({
                            marginBottom: 0.5,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? theme.palette.background.paper
                                : "#e1edf0",
                            color:
                              theme.palette.mode === "dark"
                                ? theme.palette.text.primary
                                : "inherit",
                          })}
                        >
                          <CardContent>
                            <ListItem
                              onClick={() => loadFullRequest(request)}
                              sx={{ padding: 0 }}
                            >
                              <ListItemText
                                primary={`Purpose: ${request.purpose.slice(
                                  0,
                                  25,
                                )}...`}
                                secondary={
                                  <>
                                    <span>
                                      Date:{" "}
                                      {new Date(
                                        request.requestDate,
                                      ).toLocaleDateString()}
                                    </span>
                                    <br />
                                    <span>Raised by: {request.requestBy}</span>
                                    <br />
                                    <span>
                                      Total Amount:{" "}
                                      {
                                        <Box
                                          component="span"
                                          sx={(theme) => ({
                                            backgroundColor:
                                              theme.palette.secondary.dark,
                                            borderRadius: "0.25rem",
                                            color: "#fff",
                                            maxWidth: "25ch",
                                            p: "0.25rem",
                                          })}
                                        >
                                          {request.requestAmount.toLocaleString(
                                            "en-SL",
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            },
                                          ) + " SLE"}
                                        </Box>
                                      }
                                    </span>
                                    <br />
                                    <span>
                                      Assigned to: {request.requestAssigned}
                                    </span>
                                    <br />
                                    <span>
                                      Status:{" "}
                                      {
                                        <Box
                                          component="span"
                                          sx={{
                                            backgroundColor: statusColor,
                                            borderRadius: "0.25rem",
                                            color: "#fff",
                                            maxWidth: "25ch",
                                            p: "0.25rem",
                                          }}
                                        >
                                          {request.requestStatus}
                                          {request.requestStatus ===
                                            "Rejected" &&
                                            request.rejectionNote && (
                                              <Tooltip
                                                title={request.rejectionNote}
                                                arrow
                                              >
                                                <IconButton
                                                  size="small"
                                                  sx={{
                                                    marginLeft: "0.5rem",
                                                    color: "#fff",
                                                  }}
                                                >
                                                  <InfoIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                        </Box>
                                      }
                                    </span>
                                  </>
                                }
                              />
                            </ListItem>
                          </CardContent>
                          <Divider />
                        </Card>
                      );
                    })}
                  </List>
                </Popover>
              )}
              {!isMobileScreen && (
                <Typography variant="caption" sx={{ paddingLeft: 2 }}>
                  {userData.DisplayName}
                </Typography>
              )}
            </Box>
          </div>
          <Box sx={{ flexGrow: 0 }} key={userImageToDisplay}>
            <Tooltip title="Personal Info">
              <IconButton
                onClick={handleOpenUserMenu}
                sx={{ p: 0 }}
                size="large"
              >
                <Avatar alt={userData.DisplayName} src={userImageToDisplay} />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={handleProfileClicked}>Profile</MenuItem>
              <MenuItem onClick={handleLogoutClicked}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: open ? drawerWidth : (theme) => theme.spacing(11),
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : (theme) => theme.spacing(11),
            boxSizing: "border-box",
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            overflowX: "hidden",
          },
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: [1],
          }}
        >
          <IconButton onClick={toggleDrawer} size="large">
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <List
          component="nav"
          sx={(theme) => ({
            overflowX: "hidden",
            overflowY: "auto",
            maxHeight: "calc(100vh - 54px)",
            scrollbarWidth: "thin",
            padding: 1,
            "&::-webkit-scrollbar": {
              width: "0.1em",
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.background.default,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.primary.main,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: theme.palette.primary.main,
            },
            "& .MuiListItem-root": {
              borderRadius: 8,
              marginBottom: 4,
              transition: "background 0.2s, color 0.2s",
              "&:hover": {
                background: theme.palette.action.hover,
                fontWeight: 600,
                color: theme.palette.primary.main,
              },
            },
            "& .Mui-selected, & .Mui-selected:hover": {
              background: theme.palette.action.selected,
              color: theme.palette.primary.main,
              fontWeight: 700,
            },
          })}
        >
          <MainListItems setButtonClick={setAreaToLoad} />
          <Divider sx={{ my: 0 }} />
          {Number(userData.CanViewReport) === 1 && (
            <>
              <SecondaryListItems setButtonClick={setAreaToLoad} />
              <Divider sx={{ my: 0 }} />
            </>
          )}
          {Number(userData.CanViewAdmin) === 1 && (
            <AdminListItems setButtonClick={setAreaToLoad} />
          )}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            width: "0.4em",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary.main,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.palette.primary.main,
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "100%",
            py: 0,
          }}
        >
          <Card
            sx={{
              width: "100%",
              //maxWidth: 1000,
              p: 0.5,
              boxShadow: 12,
              borderRadius: 3,
              bgcolor: (theme) => theme.palette.background.paper,
            }}
          >
            <Suspense
              fallback={
                <Typography variant="body2" sx={{ p: 2 }}>
                  Loading...
                </Typography>
              }
            >
              {areaToLoad}
            </Suspense>
          </Card>
        </Box>
        {/* Modals remain outside the card */}
        <Suspense
          fallback={
            <Typography variant="body2" sx={{ p: 2 }}>
              Loading module...
            </Typography>
          }
        >
          {isWizardOpen && (
            <ClaimWizard openUser={openClaim} onClose={handleCloseWizard} />
          )}
          {openPR && (
            <AddExpenseRequest
              openUser={openPR}
              onClose={handleClose}
              requestRow={requestRow}
            />
          )}
        </Suspense>
      </Box>
    </Box>
  );
};

export default function Dashboard() {
  if (typeof performance !== "undefined") {
    performance.mark("dashboard_component_export");
    // Create a measure after a tick to allow marks to register
    setTimeout(() => {
      try {
        performance.measure(
          "dashboard_initial_render",
          "dashboard_module_loaded",
          "dashboard_content_before_return",
        );
        const entries = performance.getEntriesByName(
          "dashboard_initial_render",
        );
        if (entries && entries.length > 0) {
          // eslint-disable-next-line no-console
          console.log(
            `[Perf] Dashboard initial render: ${entries[0].duration.toFixed(
              1,
            )} ms`,
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Performance measurement failed", e);
      }
    }, 0);
  }
  return <DashboardContent />;
}
